// layerVisibilityStore — layer visibility map + toggle logic.
//
// Extracted from uiStore.ts as step 2d of HI-2 (audit 2026-05-24).
// This is the largest step of the HI-2 step 2 uiStore split: the
// `layers` visibility map + `toggleLayer` + `isLayerVisible` move here
// so the ~47 consumer sites read/write from a focused domain store
// instead of the monolithic uiStore.
//
// Persistence: reads/writes the shared `fyrefly.ui.v1` localStorage
// blob via the same prefsSync helpers uiStore uses. The `layers` key
// is a TOP-LEVEL field on PersistedUiPrefs (not nested like wind prefs).
// No localStorage key namespace changes — existing users keep their
// persisted layer visibility after the upgrade.
//
// Cloud-sync: the PersistedUiPrefs schema in prefsSync.ts already holds
// the top-level `layers` field. On pull (sign-in), prefsSync.ts applies
// it to this store via a lazy import. No Supabase schema migration
// needed — the `prefs` jsonb column is schema-less.
//
// toggleLayer has THREE side effects that are copied byte-for-byte from
// the uiStore implementation:
//   1. Exclusive-group enforcement — if turning ON a layer in an
//      exclusive group (e.g. 'heavy-overlay'), all siblings in that
//      group are forced OFF in the same update.
//   2. Persistence — persistUiPrefs({ layers: updated }) so the
//      visibility map survives page reload.
//   3. Audit event — flight.weather_viewed fires only when a
//      wx-bearing layer transitions OFF → ON (not ON→ON re-runs, not
//      OFF transitions). WX_LAYER_GROUPS determines which groups count.
//
// mergeWithDefaultLayers is also moved here. It ensures that new layers
// added to LAYER_CATALOG after the user's last save appear in their
// correct defaultOn state (rather than being silently absent).
//
// Uses subscribeWithSelector middleware for fine-grained field-level
// subscriptions (audit synthesis HI-2 goal).

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  LAYER_CATALOG,
  type LayerId,
  isExclusiveGroup,
  getLayerDef,
} from '../map/layerCatalog';
import { trackAuditEvent } from '../lib/audit';
import { loadUiPrefs, persistUiPrefs } from './prefsSync';

// ── Types ────────────────────────────────────────────────────────────

export type LayerVisibility = Record<LayerId, boolean>;

export interface LayerVisibilityState {
  /** Visibility map for every catalog layer. */
  layers: LayerVisibility;

  /**
   * Toggle a single layer. If the layer belongs to an exclusive group
   * and we're turning it ON, every other layer in the same group is
   * forced OFF in the same update. Pass `next` to force a value
   * (useful for batch operations).
   */
  toggleLayer: (id: LayerId, next?: boolean) => void;

  /** Convenience: read visibility of one layer. */
  isLayerVisible: (id: LayerId) => boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────

function defaultLayers(): LayerVisibility {
  const m = {} as LayerVisibility;
  for (const l of LAYER_CATALOG) m[l.id] = l.defaultOn;
  return m;
}

/**
 * Merge persisted layer visibility with the catalog's defaults so
 * new layers added after the user's last save still show their
 * default-on state instead of silently being missing.
 */
export function mergeWithDefaultLayers(saved?: LayerVisibility): LayerVisibility {
  const defaults = defaultLayers();
  if (!saved) return defaults;
  const merged = { ...defaults };
  for (const id of Object.keys(saved) as LayerId[]) {
    // Only honor saved values for layer ids that still exist in the
    // catalog; drop any that have been retired.
    if (id in defaults) {
      merged[id] = !!saved[id];
    }
  }
  return merged;
}

// ── Constants ────────────────────────────────────────────────────────

/**
 * Layer groups that count as "weather viewing" for the audit trail.
 * Toggling one ON emits a `flight.weather_viewed` event so the audit
 * log captures that the pilot reviewed live wx data outside the
 * formal briefing path.
 */
const WX_LAYER_GROUPS = new Set(['weather', 'gfa']);

/**
 * Cross-group pairwise mutual exclusion. Some layers live in different
 * groups (so the group-level mutex doesn't catch them) but are still
 * visually incompatible — e.g. animated wind particles (white→orange
 * sprites in the 'heavy-overlay' group) and the HRRR smoke raster
 * (yellow→brown plume in the 'weather' group) both paint warm colors
 * over the same airspace and stomp on each other's color coding.
 *
 * Each pair is bidirectional: turning EITHER member ON forces the
 * other OFF in the same update. (Boss 2026-05-29.)
 */
const MUTEX_PAIRS: ReadonlyArray<readonly [LayerId, LayerId]> = [
  ['wind', 'hrrr-smoke'],
];

/** Resolve every layer that should be forced OFF when `id` is
 *  turned ON via pairwise mutex (not group mutex — that's handled
 *  separately in toggleLayer). */
function pairwiseMutexPartners(id: LayerId): LayerId[] {
  const partners: LayerId[] = [];
  for (const [a, b] of MUTEX_PAIRS) {
    if (a === id) partners.push(b);
    else if (b === id) partners.push(a);
  }
  return partners;
}

// ── Initialization ───────────────────────────────────────────────────

// Hydrate from the shared prefs blob so the first render already
// reflects the user's saved choices (no default-then-snap flicker).
const _prefs = loadUiPrefs();

// ── Store ────────────────────────────────────────────────────────────

export const useLayerVisibilityStore = create<LayerVisibilityState>()(
  subscribeWithSelector((set, get) => ({
    layers: mergeWithDefaultLayers(_prefs.layers),

    toggleLayer: (id, next) => set((s) => {
      const def = getLayerDef(id);
      const prev = s.layers[id];
      const target = next ?? !prev;
      const updated: LayerVisibility = { ...s.layers, [id]: target };

      // If turning ON in an exclusive group, force all siblings OFF.
      if (target && isExclusiveGroup(def.group)) {
        for (const other of LAYER_CATALOG) {
          if (other.group === def.group && other.id !== id) {
            updated[other.id] = false;
          }
        }
      }

      // Pairwise (cross-group) mutex — see MUTEX_PAIRS above.
      // Force every declared partner OFF when this layer turns ON.
      if (target) {
        for (const partner of pairwiseMutexPartners(id)) {
          updated[partner] = false;
        }
      }

      persistUiPrefs({ layers: updated });

      // Audit: only when a wx-bearing layer transitions OFF → ON. Skip
      // ON → ON re-runs and any OFF transition. Fire-and-forget.
      if (target && !prev && WX_LAYER_GROUPS.has(def.group as string)) {
        trackAuditEvent({
          kind: 'flight.weather_viewed',
          subjectKind: 'layer',
          subjectId: id,
          payload: {
            layerId: id,
            layerLabel: def.label ?? id,
            group: def.group,
            via: 'map_layer_toggle',
          },
        });
      }

      return { layers: updated };
    }),

    isLayerVisible: (id) => get().layers[id],
  })),
);
